import React, { useState, useRef, useEffect } from 'react';
import styles from './windows-xp-modal.module.scss';
import { classnames } from '../../common/general-utilities/general-utilities';
import {
  getCenteredPosition,
  OS_RESUME_WINDOWS,
  TASKBAR_HEIGHT,
} from '../../common/general-utilities/os-utilities';
import { IDefaultResponsiveSupport } from '../../common/interfaces/common.interface';
import { SoundType } from '../../common/services/systemSoundsService';
interface WindowsXPModalProps extends IDefaultResponsiveSupport {
  isOpen: boolean;
  minimized?: boolean;
  isActive: boolean;
  onActivate: () => void;
  onMinimize: () => void;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  openWindowsCount: number;
  zIndex: number;
  Icon?: JSX.Element;
  play: (type: SoundType) => void;
  width?: string;
  height?: string;
  isOverflowHidden?: boolean;
  handleShutdown?: () => void;
}

const WindowsXPModal: React.FC<WindowsXPModalProps> = ({
  isOpen,
  minimized = false,
  isActive,
  onActivate,
  onMinimize,
  onClose,
  title = 'Resume OS Modal',
  children,
  openWindowsCount = 0,
  zIndex,
  responsiveMode,
  Icon = null,
  play,
  width,
  height,
  isOverflowHidden = false,
  handleShutdown,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const [position, setPosition] = useState(() =>
    getCenteredPosition(openWindowsCount),
  );
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [maximized, setMaximized] = useState(false);
  const [prevPosition, setPrevPosition] = useState(position);

  /* ------------------ DRAG LOGIC ------------------ */

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragging || !modalRef.current || maximized) return;

      const modalWidth = modalRef.current.offsetWidth;
      const modalHeight = modalRef.current.offsetHeight;

      let newX = e.clientX - offset.x;
      let newY = e.clientY - offset.y;

      const maxX = window.innerWidth - modalWidth;
      const maxY = window.innerHeight - TASKBAR_HEIGHT - modalHeight;

      newX = Math.max(0, Math.min(newX, maxX));
      newY = Math.max(0, Math.min(newY, maxY));

      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => setDragging(false);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, offset, maximized]);

  /* ------------------ ACTIVATE ------------------ */

  const bringToFront = () => {
    onActivate();
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!modalRef.current || maximized) return;

    bringToFront();

    const rect = modalRef.current.getBoundingClientRect();
    setOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });

    setDragging(true);
  };

  /* ------------------ MAXIMIZE ------------------ */

  const handleMaximize = (maxi?: boolean) => {
    !maxi && play('click');
    bringToFront();

    if (!maximized || maxi) {
      setPrevPosition(position);
      setMaximized(true);
    } else {
      setMaximized(false);
      setPosition(prevPosition);
    }
  };

  const handleCloseBtn = () => {
    play('click');
    onClose();
  };

  const handleMinimizeBtn = () => {
    play('click');
    onMinimize();
  };

  useEffect(() => {
    if (title === OS_RESUME_WINDOWS.SHARE && onClose) {
      onClose();
    }
    if (title === OS_RESUME_WINDOWS.SHUTDOWN && handleShutdown) {
      handleShutdown();
    }
  }, [title, onClose, handleShutdown]);

  useEffect(() => {
    if (responsiveMode !== 'desktop') {
      handleMaximize(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responsiveMode]);

  if (!isOpen || minimized) return null;

  return (
    <div
      ref={modalRef}
      className={classnames(styles.modal, {
        [styles.active]: isActive,
        [styles.maximized]: maximized,
      })}
      style={{
        zIndex,
        ...(maximized ? {} : { top: position.y, left: position.x }),
        width,
        height,
      }}
      onMouseDown={bringToFront}
    >
      <div className={styles.titleBar} onMouseDown={handleMouseDown}>
        <span className={styles.title}>
          {Icon}
          {title}
        </span>

        <div className={styles.controls}>
          <button
            className={`${styles.controlBtn} ${styles.minimize}`}
            onClick={handleMinimizeBtn}
          />
          <button
            className={`${styles.controlBtn} ${styles.maximize}`}
            onClick={() => handleMaximize()}
            disabled={responsiveMode !== 'desktop'}
          />
          <button
            className={`${styles.controlBtn} ${styles.close}`}
            onClick={handleCloseBtn}
          />
        </div>
      </div>

      <div
        className={classnames(styles.content, {
          maximizedWindow: maximized,
          [styles.hiddenOverflow]: isOverflowHidden,
        })}
      >
        {children}
      </div>
    </div>
  );
};

export default WindowsXPModal;
